import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, CheckCheck, CircleDot, Cloud, CloudAlert, CloudOff, CloudUpload, FileDiff, ListTree, Loader2, X } from "lucide-react";
import type { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";
import { cn } from "@/lib/utils";
import type { SaveStatus } from "@/lib/useSaveTracker";
import type { DocRevision, DocStats, OutlineItem } from "./docInsights";

const closeButtonClass =
    "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

// ── Outline (left) ──────────────────────────────────────────────────────────

export function OutlinePane({ items, currentKey, onNavigate, onClose }: {
    items: OutlineItem[];
    currentKey: string | null;
    onNavigate: (item: OutlineItem) => void;
    onClose: () => void;
}) {
    const minLevel = items.reduce((m, i) => Math.min(m, i.level), 9);
    // Mock `.side` + `.outline`: uppercase heading, 13px items, sub-levels
    // indented and muted, the current heading filled with the accent tint.
    return (
        <aside
            aria-labelledby="docs-outline-title"
            className="absolute inset-y-0 left-0 z-30 flex w-64 max-w-[85vw] flex-col border-r bg-[var(--docs-pane)] text-foreground shadow-xl md:static md:z-auto md:w-[232px] md:shrink-0 md:shadow-none"
        >
            <div className="flex h-10 shrink-0 items-center justify-between gap-2 pl-3.5 pr-2">
                <h2 id="docs-outline-title" className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <ListTree className="h-3.5 w-3.5" aria-hidden />Outline
                </h2>
                <button type="button" onClick={onClose} aria-label="Close outline" title="Close outline" className={closeButtonClass}>
                    <X className="h-4 w-4" aria-hidden />
                </button>
            </div>
            {items.length === 0 ? (
                <p className="px-3.5 py-1 text-[13px] leading-5 text-muted-foreground">
                    Headings you add (Heading 1–9 styles on the Home tab) appear here.
                </p>
            ) : (
                <nav aria-label="Document headings" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
                    <ul className="space-y-px">
                        {items.map((item) => {
                            const current = item.key === currentKey;
                            const depth = Math.min(6, item.level - minLevel);
                            return (
                                <li key={item.key}>
                                    <button
                                        type="button"
                                        onClick={() => onNavigate(item)}
                                        aria-current={current ? "location" : undefined}
                                        title={item.text}
                                        className={cn(
                                            "block w-full truncate rounded py-[5px] pr-2 text-left text-[13px] leading-5 hover:bg-[var(--docs-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                                            depth === 0 ? "text-foreground" : "text-muted-foreground",
                                            current && "bg-[var(--docs-selected)] font-semibold text-foreground hover:bg-[var(--docs-selected)]"
                                        )}
                                        style={{ paddingLeft: 8 + depth * 14 }}
                                    >
                                        {item.text}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            )}
        </aside>
    );
}

// ── Suggestions / review (right) ────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
    Insertion: "Inserted",
    Deletion: "Deleted",
    MoveTo: "Moved here",
    MoveFrom: "Moved from here"
};

function formatWhen(date: string): string {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function ReviewPane({
    revisions, canAct, suggesting, onAccept, onReject, onAcceptAll, onRejectAll, onSelect, onClose
}: {
    revisions: DocRevision[];
    /** Editors can accept / reject; viewers (and Viewing mode) only see. */
    canAct: boolean;
    suggesting: boolean;
    onAccept: (rev: DocRevision) => void;
    onReject: (rev: DocRevision) => void;
    onAcceptAll: () => void;
    onRejectAll: () => void;
    onSelect: (rev: DocRevision) => void;
    onClose: () => void;
}) {
    return (
        <aside
            aria-labelledby="docs-review-title"
            className="absolute inset-y-0 right-0 z-30 flex w-80 max-w-[90vw] flex-col border-l bg-background text-foreground shadow-xl md:static md:z-auto md:shrink-0 md:shadow-none"
        >
            <div className="flex h-10 shrink-0 items-center justify-between border-b px-3">
                <h2 id="docs-review-title" className="flex items-center gap-2 text-sm font-semibold">
                    <FileDiff className="h-4 w-4" aria-hidden />Suggestions
                    <span className="rounded-full bg-muted px-1.5 text-xs font-medium text-muted-foreground">{revisions.length}</span>
                </h2>
                <button type="button" onClick={onClose} aria-label="Close suggestions" title="Close suggestions" className={closeButtonClass}>
                    <X className="h-4 w-4" aria-hidden />
                </button>
            </div>
            {!canAct ? (
                <p className="border-b px-3 py-2 text-xs text-muted-foreground">
                    You can see suggestions here. Switch to Editing or Suggesting to accept or reject them.
                </p>
            ) : !suggesting ? (
                <p className="border-b px-3 py-2 text-xs text-muted-foreground">
                    Switch to <strong>Suggesting</strong> (top right) to record your own edits as suggestions.
                </p>
            ) : null}
            <div className="flex shrink-0 gap-2 border-b px-3 py-2">
                <button
                    type="button"
                    onClick={onAcceptAll}
                    disabled={!canAct || revisions.length === 0}
                    className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
                >
                    <CheckCheck className="h-4 w-4" aria-hidden />Accept all
                </button>
                <button
                    type="button"
                    onClick={onRejectAll}
                    disabled={!canAct || revisions.length === 0}
                    className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50"
                >
                    <X className="h-4 w-4" aria-hidden />Reject all
                </button>
            </div>
            {revisions.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No suggestions in this document.</p>
            ) : (
                <ul aria-label="Suggested changes" className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
                    {revisions.map((rev, i) => (
                        <RevisionCard
                            key={`${rev.revisionID}-${i}`}
                            rev={rev}
                            canAct={canAct}
                            onAccept={() => onAccept(rev)}
                            onReject={() => onReject(rev)}
                            onSelect={() => onSelect(rev)}
                        />
                    ))}
                </ul>
            )}
        </aside>
    );
}

function RevisionCard({ rev, canAct, onAccept, onReject, onSelect }: {
    rev: DocRevision;
    canAct: boolean;
    onAccept: () => void;
    onReject: () => void;
    onSelect: () => void;
}) {
    const type = String(rev.revisionType);
    const label = TYPE_LABEL[type] ?? type;
    const author = rev.author || "Unknown author";
    let content = "";
    try {
        content = (rev.getContent() ?? "").replace(/\s+/g, " ").trim();
    } catch {
        content = "";
    }
    const excerpt = content.length > 240 ? `${content.slice(0, 240)}…` : content;
    return (
        <li className="rounded-md border bg-background p-2.5 text-sm shadow-sm">
            <button
                type="button"
                onClick={onSelect}
                aria-label={`${label} by ${author}: ${excerpt || "formatting or object change"}. Show in document`}
                className="block w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <span className="flex items-center gap-2">
                    <span aria-hidden className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                        {author.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{author}</span>
                        <span className="block text-xs text-muted-foreground">{formatWhen(rev.date)}</span>
                    </span>
                </span>
                <span className="mt-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
                <span
                    className={cn(
                        "mt-0.5 block break-words",
                        type === "Deletion" || type === "MoveFrom" ? "text-red-700 line-through dark:text-red-400" : "text-green-700 dark:text-green-400"
                    )}
                >
                    {excerpt || <em className="text-muted-foreground">Formatting or object change</em>}
                </span>
            </button>
            {canAct ? (
                <div className="mt-2 flex justify-end gap-1">
                    <button
                        type="button"
                        onClick={onAccept}
                        aria-label={`Accept: ${label} by ${author}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-green-700 hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-green-400 dark:hover:bg-green-950/40"
                    >
                        <Check className="h-3.5 w-3.5" aria-hidden />Accept
                    </button>
                    <button
                        type="button"
                        onClick={onReject}
                        aria-label={`Reject: ${label} by ${author}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-red-400 dark:hover:bg-red-950/40"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden />Reject
                    </button>
                </div>
            ) : null}
        </li>
    );
}

// ── Status bar additions ────────────────────────────────────────────────────

/**
 * Renders into the editor's own status bar (whose "Page N of M", layout
 * buttons and zoom the container already provides). "start" goes right after
 * the page number; "end" is pushed right, just before the layout buttons and
 * zoom. "start" falls back to a bar of our own if the editor's isn't there;
 * "end" then renders nothing.
 */
export function NativeStatusBarSlot({ container, placement = "start", children }: {
    container: DocumentEditorContainerComponent | null;
    placement?: "start" | "end";
    children: ReactNode;
}) {
    const [slot, setSlot] = useState<HTMLElement | null>(null);
    const [fallback, setFallback] = useState(false);
    useEffect(() => {
        if (!container) return;
        let cancelled = false;
        let tries = 0;
        let el: HTMLDivElement | null = null;
        let timer = 0;
        const attach = () => {
            if (cancelled) return;
            const root = (container as unknown as { element?: HTMLElement }).element;
            const bar = root?.querySelector(".e-de-status-bar");
            if (!bar) {
                if (++tries < 20) timer = window.setTimeout(attach, 100);
                else setFallback(true);
                return;
            }
            el = document.createElement("div");
            if (placement === "end") {
                el.className = "athavita-doc-status athavita-doc-status-end";
                el.style.cssText = "display:flex;align-items:center;flex:none;margin:0 6px 0 auto;";
                const right = bar.querySelector(".e-de-statusbar-separator, .e-de-statusbar-pageweb, .e-de-statusbar-zoom");
                if (right) right.insertAdjacentElement("beforebegin", el);
                else bar.append(el);
            } else {
                el.className = "athavita-doc-status";
                el.style.cssText = "display:flex;align-items:center;min-width:0;margin:0 12px;";
                // With spell check off (our default) the page number is `-spellout`.
                const pageNumber = bar.querySelector(".e-de-ctnr-pg-no, .e-de-ctnr-pg-no-spellout");
                if (pageNumber) pageNumber.insertAdjacentElement("afterend", el);
                else bar.prepend(el);
            }
            setSlot(el);
        };
        attach();
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            el?.remove();
            setSlot(null);
        };
    }, [container, placement]);
    if (slot) return createPortal(children, slot);
    if (fallback && placement === "start") {
        return <div className="flex h-8 shrink-0 items-center border-t bg-background px-3">{children}</div>;
    }
    return null;
}

/** Cloud with a tick — lucide-react 0.468 has no CloudCheck. */
function CloudSaved() {
    return (
        <span aria-hidden className="relative inline-flex h-3.5 w-3.5 shrink-0">
            <Cloud className="h-3.5 w-3.5" />
            <Check className="absolute left-1/2 top-[58%] h-2 w-2 -translate-x-1/2 -translate-y-1/2" strokeWidth={3} />
        </span>
    );
}

/**
 * The save state for the status bar (mock: "Saved to Drive" before zoom) —
 * the same tracker the title uses; failed saves retry on click.
 */
export function DocSaveStatusItem({ status, inDrive, loading, onRetry }: {
    status: SaveStatus;
    inDrive: boolean;
    loading: boolean;
    onRetry: () => void;
}) {
    const base = "inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded px-1.5 text-xs";
    const muted = cn(base, "text-muted-foreground");
    const label = (text: string) => <span className="max-sm:sr-only">{text}</span>;
    let content: ReactNode;
    if (loading) {
        content = <span className={muted}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />{label("Opening…")}</span>;
    } else if (status === "saving") {
        content = <span className={muted}><CloudUpload className="h-3.5 w-3.5" aria-hidden />{label("Saving…")}</span>;
    } else if (status === "failed") {
        content = (
            <button
                type="button"
                onClick={onRetry}
                title="Try saving again"
                className={cn(base, "font-medium text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary")}
            >
                <CloudAlert className="h-3.5 w-3.5" aria-hidden />{label("Save failed — retry")}
            </button>
        );
    } else if (status === "dirty") {
        content = <span className={muted}><CircleDot className="h-3.5 w-3.5" aria-hidden />{label("Unsaved changes")}</span>;
    } else if (inDrive) {
        content = <span className={muted}><CloudSaved />{label("Saved to Drive")}</span>;
    } else {
        content = (
            <span className={muted} title="Save (Ctrl+S) to keep this document in Drive">
                <CloudOff className="h-3.5 w-3.5" aria-hidden />{label("Not in Drive")}
            </span>
        );
    }
    return <div role="status" aria-live="polite" className="flex items-center">{content}</div>;
}

export function DocStatusItems({ stats, selectionWords, outlineOpen, onToggleOutline, suggestionCount, reviewOpen, onToggleReview }: {
    stats: DocStats;
    selectionWords: number | null;
    outlineOpen: boolean;
    onToggleOutline: () => void;
    suggestionCount: number;
    reviewOpen: boolean;
    onToggleReview: () => void;
}) {
    const fmt = (n: number) => n.toLocaleString();
    const toggleClass = (on: boolean) => cn(
        "inline-flex h-6 items-center gap-1 rounded px-1.5 text-xs hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        on ? "bg-primary/10 text-primary" : "text-muted-foreground"
    );
    return (
        <div className="flex min-w-0 items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
            <button type="button" onClick={onToggleOutline} aria-pressed={outlineOpen} title="Show the document outline" className={toggleClass(outlineOpen)}>
                <ListTree className="h-3.5 w-3.5" aria-hidden /><span className="hidden sm:inline">Outline</span>
                <span className="sr-only sm:hidden">Outline</span>
            </button>
            <span aria-hidden className="h-3.5 w-px bg-border" />
            <span title={selectionWords !== null ? "Words in the selection / in the document" : "Words in the document"}>
                {selectionWords !== null ? `${fmt(selectionWords)} of ${fmt(stats.words)} words` : `${fmt(stats.words)} words`}
            </span>
            <span className="hidden sm:inline" title={`${fmt(stats.charactersNoSpaces)} characters without spaces`}>
                {fmt(stats.characters)} characters
            </span>
            <span aria-hidden className="h-3.5 w-px bg-border" />
            <button type="button" onClick={onToggleReview} aria-pressed={reviewOpen} title="Suggested changes (track changes)" className={toggleClass(reviewOpen)}>
                <FileDiff className="h-3.5 w-3.5" aria-hidden />
                <span className="hidden sm:inline">Suggestions</span>
                <span className="sr-only sm:hidden">Suggestions</span>
                {suggestionCount > 0 ? (
                    <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground">{suggestionCount}</span>
                ) : null}
            </button>
        </div>
    );
}
